import { createAction, createAsyncThunk, unwrapResult } from '@reduxjs/toolkit';
import { createHash } from 'crypto-pro';

import { PROFILE_INFO_ERROR } from '../../constants';
import AccountsApi from '../../services/api/AccountsApi';
import AuthApi from '../../services/api/AuthApi';
import CompanyApi from '../../services/api/CompanyApi';
import { SetProfileRequest } from '../../services/api/dto/Accounts';
import SignupStorage from '../../services/SignupStorage';
import { COMPANY_UPLOAD_DOC_TYPE, REGISTRATION_STEP_STATUS } from '../../types';
import { RootState } from '../../types';
import { showError } from '../../utils/utils';

import { setPush } from './app';
import { setToken } from './auth';

const setSignupLoading = createAction<{
  value: boolean;
  name:
    | 'checkStep'
    | 'initRegistration'
    | 'code'
    | 'profileInfo'
    | 'uploadDoc'
    | 'checkUploadedDocs'
    | 'uploadAgreement'
    | 'getAccessionDocText'
    | 'getAccessionDoc'
    | 'uploadAccessionDoc'
    | 'setPassword';
}>('signup/setLoading');
const getStorageData = createAction('signup/getStorageData');

const checkRegistrationStep = createAsyncThunk(
  'signup/checkRegistrationStep',
  async (
    payload: {
      token: string;
      unAuthCb?: () => void;
    },
    thunkAPI
  ) => {
    try {
      thunkAPI.dispatch(setSignupLoading({ value: true, name: 'checkStep' }));
      const response = await AccountsApi.getProfileInfo(payload.token);
      if (response.status === 200) {
        const storageData = localStorage.getItem('signup');
        if (storageData) {
          localStorage.setItem(
            'signup',
            JSON.stringify({
              ...JSON.parse(storageData),
              step: response.data.status,
            })
          );
        }
        return response.data;
      }
    } catch (e: any) {
      if (e.response.status === 404 && payload.unAuthCb) {
        payload.unAuthCb();
      }
    } finally {
      thunkAPI.dispatch(setSignupLoading({ value: false, name: 'checkStep' }));
    }
    return undefined;
  }
);

const initRegistration = createAsyncThunk(
  'signup/initRegistration',
  async (payload: { phoneNumber: string }, thunkAPI) => {
    try {
      thunkAPI.dispatch(
        setSignupLoading({ value: true, name: 'initRegistration' })
      );
      const response = await AuthApi.registration(payload);
      if (response.status === 200) {
        localStorage.setItem(
          'signup',
          JSON.stringify({
            phone: payload.phoneNumber,
            verificationCodeId: response.data.id,
            codeLength: response.data.code.length,
          })
        );
        return { ...response.data, phone: payload.phoneNumber };
      }
    } catch (err) {
      showError(err, thunkAPI);
    } finally {
      thunkAPI.dispatch(
        setSignupLoading({ value: false, name: 'initRegistration' })
      );
    }
    return undefined;
  }
);

const registrationCode = createAsyncThunk(
  'signup/registrationCode',
  async (
    payload: { code: string; cb?: (step: REGISTRATION_STEP_STATUS) => void },
    thunkAPI
  ) => {
    thunkAPI.dispatch(setSignupLoading({ name: 'code', value: true }));
    const signupState = (thunkAPI.getState() as RootState).signup;
    if (signupState.registrationId) {
      try {
        const response = await AuthApi.login(
          signupState.registrationId,
          payload.code
        );
        if (response.status === 200) {
          thunkAPI.dispatch(setToken(response.data.jwt));
          SignupStorage.setKeyStorage('token', response.data.jwt);
          thunkAPI
            .dispatch(checkRegistrationStep({ token: response.data.jwt }))
            .then(unwrapResult)
            .then((res) => {
              if (payload.cb && res)
                payload.cb(res.status as REGISTRATION_STEP_STATUS);
            });
        }
      } catch (e: any) {
        if (e.response.status === 404 && signupState.phone) {
          thunkAPI
            .dispatch(initRegistration({ phoneNumber: signupState.phone }))
            .then((action) => {
              if (action.meta.requestStatus === 'fulfilled') {
                thunkAPI.dispatch(registrationCode({ ...payload }));
              }
            });
        }
      } finally {
        thunkAPI.dispatch(setSignupLoading({ name: 'code', value: false }));
      }
    }
    return undefined;
  }
);

const setProfileInfo = createAsyncThunk(
  'signup/setProfileInfo',
  async (payload: SetProfileRequest, thunkAPI) => {
    try {
      setSignupLoading({ value: true, name: 'profileInfo' });
      const store = thunkAPI.getState() as RootState;
      const { token } = store.auth.account;
      const response = await AccountsApi.setProfileInfo(token, payload);
      if (response.status === 200) {
        return response.data;
      }
    } catch (e: any) {
      if (e.response.status === 409) {
        thunkAPI.dispatch(
          setPush({ message: PROFILE_INFO_ERROR.DUPLICATE_DATA, type: 'error' })
        );
      }
      throw e;
    } finally {
      setSignupLoading({ value: false, name: 'profileInfo' });
    }
    return undefined;
  }
);

const uploadProfileDoc = createAsyncThunk(
  'signup/uploadDoc',
  async (
    payload: {
      type: COMPANY_UPLOAD_DOC_TYPE;
      doc: File;
    },
    thunkAPI
  ) => {
    try {
      thunkAPI.dispatch(setSignupLoading({ name: 'uploadDoc', value: true }));
      const { token } = (thunkAPI.getState() as RootState).auth.account;
      const response = await AccountsApi.uploadProfileDoc(token, payload);
      if (response.status === 200) {
        thunkAPI.dispatch(
          setPush({ message: 'Файл успешно загружен', type: 'success' })
        );
        return { ...response.data, fileName: payload.doc.name };
      }
    } catch (err) {
      showError(err, thunkAPI);
    } finally {
      thunkAPI.dispatch(setSignupLoading({ name: 'uploadDoc', value: false }));
    }
    return undefined;
  }
);

const setPubKey = createAsyncThunk(
  'signup/setPubKey',
  async (
    payload: {
      pubKey: string;
    },
    thunkAPI
  ) => {
    try {
      thunkAPI.dispatch(setSignupLoading({ name: 'uploadDoc', value: true }));
      const { token } = (thunkAPI.getState() as RootState).auth.account;
      const response = await AccountsApi.setPubKey(token, payload);
      if (response.status === 200) {
        thunkAPI.dispatch(
          setPush({
            message: 'Публичный Ключ успешно сохранён',
            type: 'success',
          })
        );
      }
    } catch (err) {
      showError(err, thunkAPI);
    } finally {
      thunkAPI.dispatch(setSignupLoading({ name: 'uploadDoc', value: false }));
    }
  }
);

const uploadAgreement = createAsyncThunk(
  'signup/uploadAgreement',
  async (_, thunkAPI) => {
    try {
      thunkAPI.dispatch(
        setSignupLoading({ name: 'uploadAgreement', value: true })
      );
      const { token } = (thunkAPI.getState() as RootState).auth.account;
      const docResponse = await AccountsApi.getUploadOnboardingDoc(token);
      if (docResponse.status === 200 && docResponse.data) {
        const response = await AccountsApi.uploadProfileDoc(token, {
          type: 'SIGNED_ONBOARDING_DOCUMENT',
          doc: new Blob([docResponse.data]),
        });
        if (response.status === 200) {
          return response.data;
        }
      }
    } catch (err) {
      showError(err, thunkAPI);
    } finally {
      thunkAPI.dispatch(
        setSignupLoading({ name: 'uploadAgreement', value: false })
      );
    }
    return undefined;
  }
);

const checkUploadedDocs = createAsyncThunk(
  'signup/checkUploadedDocs',
  async (_, thunkAPI) => {
    try {
      thunkAPI.dispatch(
        setSignupLoading({ value: true, name: 'checkUploadedDocs' })
      );
      const { token } = (thunkAPI.getState() as RootState).auth.account;
      const response = await CompanyApi.getCompanyInfo(token);

      if (response.status === 200) {
        return response.data;
      }
    } catch (err) {
      showError(err, thunkAPI);
    } finally {
      thunkAPI.dispatch(
        setSignupLoading({ value: false, name: 'checkUploadedDocs' })
      );
    }
    return undefined;
  }
);

const getAccessionDocText = createAsyncThunk(
  'signup/getAccessionDocText',
  async (_, thunkAPI) => {
    try {
      thunkAPI.dispatch(
        setSignupLoading({ value: true, name: 'getAccessionDocText' })
      );
      const { token } = (thunkAPI.getState() as RootState).auth.account;
      const response = await AccountsApi.getAccessionDocText(token);
      if (response.status === 200) {
        return response.data;
      }
    } catch (err) {
      showError(err, thunkAPI);
    } finally {
      thunkAPI.dispatch(
        setSignupLoading({ value: false, name: 'getAccessionDocText' })
      );
    }

    return undefined;
  }
);

const getAccessionDoc = createAsyncThunk(
  'signup/getAccessionDoc',
  async (_, thunkApi) => {
    try {
      thunkApi.dispatch(
        setSignupLoading({ value: true, name: 'getAccessionDoc' })
      );
      const { token } = (thunkApi.getState() as RootState).auth.account;
      const response = await AccountsApi.getAccessionDoc(token);
      if (response.status === 200 && response.data) {
        const bufferHash = createHash(response.data);
        return {
          doc: btoa(String.fromCharCode(...new Uint8Array(response.data))),
          hash: bufferHash,
        };
      }
    } catch (err) {
      showError(err, thunkApi);
    } finally {
      thunkApi.dispatch(
        setSignupLoading({ value: false, name: 'getAccessionDoc' })
      );
    }
    return undefined;
  }
);

const uploadAccessionDoc = createAsyncThunk(
  'signup/uploadAccessionDoc',
  async (_, thunkAPI) => {
    try {
      thunkAPI.dispatch(
        setSignupLoading({ value: true, name: 'uploadAccessionDoc' })
      );
      const { token } = (thunkAPI.getState() as RootState).auth.account;
      const docResponse = await AccountsApi.getAccessionDoc(token);
      if (docResponse.status === 200 && docResponse.data) {
        const response = await AccountsApi.uploadProfileDoc(token, {
          type: 'SIGNED_ACCESSION_AGREEMENT',
          doc: new Blob([docResponse.data]),
        });
        if (response.status === 200) {
          return response.data;
        }
      }
    } catch (err) {
      showError(err, thunkAPI);
    } finally {
      thunkAPI.dispatch(
        setSignupLoading({ value: false, name: 'uploadAccessionDoc' })
      );
    }

    return undefined;
  }
);

const setProfilePassword = createAsyncThunk(
  'signup/setProfilePassword',
  async (payload: { password: string }, thunkAPI) => {
    try {
      thunkAPI.dispatch(setSignupLoading({ name: 'setPassword', value: true }));
      const { token } = (thunkAPI.getState() as RootState).auth.account;
      const response = await AccountsApi.setProfilePassword(token, payload);
      if (response.status === 200) {
        SignupStorage.clearStorage();
        return response.data;
      }
    } catch (err) {
      showError(err, thunkAPI);
    } finally {
      thunkAPI.dispatch(
        setSignupLoading({ name: 'setPassword', value: false })
      );
    }
    return undefined;
  }
);

export {
  checkRegistrationStep,
  setSignupLoading,
  initRegistration,
  getStorageData,
  registrationCode,
  setProfileInfo,
  uploadProfileDoc,
  checkUploadedDocs,
  uploadAgreement,
  getAccessionDocText,
  uploadAccessionDoc,
  setPubKey,
  setProfilePassword,
  getAccessionDoc,
};
